import React from "react"
import { css } from "@emotion/react"
import Chevron from "@myvp/shared/src/icons/chevron"
import { fontStyleItalicCss, Medium14 } from "@myvp/shared/src/typography"
import { PrimitiveType, useIntl } from "react-intl"
// oxlint-disable-next-line no-restricted-imports
import { Link, To, useLocation, useMatches } from "react-router"
import { I18nMessageId } from "src/i18n/i18n-message-types"
import { RouteHandle } from "src/pages/study/create-study-routes"
import { useQueries } from "@tanstack/react-query"
import { useRouteParams } from "@myvp/shared/src/hooks/use-route-params"
import studyRouteNames from "src/pages/study/study-route-names"
import {
  dimensionTitleMap,
  ComplianceDimensionKey,
} from "src/pages/study/compliance/compliance.model"

const DisabledCrumb = (props: { text: React.ReactNode }) => (
  <Medium14
    css={[
      fontStyleItalicCss,
      (theme) => css`
        color: ${theme.palette.content.neutral.readonlysubtle};
      `,
    ]}
  >
    {props.text}
  </Medium14>
)

const LinkCrumb = (props: { to: To; text: React.ReactNode }) => (
  <Link
    to={props.to}
    css={(theme) => css`
      color: ${theme.palette.content.neutral.subtle};
      text-decoration: none;
    `}
  >
    <Medium14>{props.text}</Medium14>
  </Link>
)

export const ComplianceDimensionCrumb = (props: {
  to: To
  disabled: boolean
}) => {
  const { complianceDimension } = useRouteParams(
    studyRouteNames.complianceDimension
  )
  const messageId =
    dimensionTitleMap[complianceDimension as ComplianceDimensionKey]

  if (!messageId) {
    throw new Error("Invalid URL Route")
  }

  return <Crumb to={props.to} disabled={props.disabled} messageId={messageId} />
}

export const Crumb = (props: {
  to: To
  data?: string
  messageId?: I18nMessageId
  messageArgs?: Record<string, PrimitiveType>
  disabled: boolean
}) => {
  const { formatMessage } = useIntl()

  const text =
    "data" in props
      ? props.data
      : formatMessage({ id: props.messageId }, props.messageArgs)

  return props.disabled ? (
    <DisabledCrumb text={text} />
  ) : (
    <LinkCrumb to={props.to} text={text} />
  )
}

const Breadcrumbs = () => {
  const matches = useMatches()
  const { search } = useLocation()
  const { formatMessage } = useIntl()

  const matchesWithCrumbs = matches.filter((match) =>
    Boolean((match.handle as RouteHandle)?.crumb)
  )

  const crumbs = useQueries({
    queries: matchesWithCrumbs
      .map((match) =>
        (match.handle as RouteHandle)?.getQueryOptions?.(match.params)
      )
      .filter((query) => !!query),
    combine: (result) => {
      const crumbs = matchesWithCrumbs.map((match, index, { length }) =>
        (match.handle as RouteHandle).crumb({
          to: { pathname: match.pathname, search },
          data: result[index]?.data,
          disabled: index === length - 1,
        })
      )
      // Force crumbs to become visible in sequential order
      const firstHiddenCrumb = result.findIndex((query) => !query.data)
      return firstHiddenCrumb === -1
        ? crumbs
        : crumbs.slice(0, firstHiddenCrumb)
    },
  })

  return (
    <nav
      aria-label={formatMessage({ id: "sponsorStudyNav.breadcrumbLabel" })}
      css={css`
        display: flex;
        gap: 8px;
        align-items: center;
        height: 18px;
      `}
    >
      {crumbs.map((crumb, index) => (
        <React.Fragment key={index}>
          {crumb}
          {index !== crumbs.length - 1 && (
            <Chevron size="small" color="disabled" />
          )}
        </React.Fragment>
      ))}
    </nav>
  )
}

export default Breadcrumbs
